import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Wand2, Mail, Phone, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { useOrg } from "@/hooks/useOrg";
import { PageHeader, RoleBadge, StatusBadge, EmptyState } from "@/components/Badges";
import { RoleSelect, OrgFields } from "@/components/OrgFields";

const genPassword = () => `Lasso-${Math.random().toString(36).slice(2, 6)}${Math.floor(1000 + Math.random() * 9000)}!`;

function ApproveDialog({ request, onClose, onDone, regions, communities }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (request) setForm({ role: request.role_requested || "", region_id: null, community_id: request.community_id, temp_password: "", google_enabled: true, note: "" });
  }, [request]);

  if (!request || !form) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/access-requests/${request.id}/approve`, { ...form, temp_password: form.temp_password || null });
      toast.success(`${request.name} approved — they can now sign in`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Approve {request.name}</DialogTitle>
          <DialogDescription>{request.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <RoleSelect value={form.role} onChange={(role) => setForm({ ...form, role })} testId="approve-role-select" />
          <OrgFields role={form.role} regionId={form.region_id} communityId={form.community_id} regions={regions} communities={communities} testPrefix="approve" onChange={(v) => setForm({ ...form, ...v })} />
          <div className="space-y-2">
            <Label>Temporary password</Label>
            <div className="flex gap-2">
              <Input value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} placeholder="Min 8 characters (optional if Google enabled)" data-testid="approve-temp-password-input" />
              <button type="button" onClick={() => setForm({ ...form, temp_password: genPassword() })} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-lasso-line px-3 text-xs font-semibold hover:border-lasso-ink" data-testid="approve-generate-password">
                <Wand2 className="h-3.5 w-3.5" /> Generate
              </button>
            </div>
          </div>
          <label className="flex items-center justify-between rounded-lg border border-lasso-line px-4 py-3 text-sm">
            <span><span className="font-semibold">Allow Google sign-in</span><br /><span className="text-xs text-muted-foreground">Sign in with the Google account for {request.email}</span></span>
            <Switch checked={form.google_enabled} onCheckedChange={(v) => setForm({ ...form, google_enabled: v })} data-testid="approve-google-switch" />
          </label>
          <div className="space-y-2">
            <Label>Note <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="approve-note-input" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold" data-testid="approve-cancel-button">Cancel</button>
          <button disabled={saving} onClick={submit} className="rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="approve-confirm-button">Approve & create user</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ request, onClose, onDone }) {
  const [note, setNote] = useState("");
  if (!request) return null;
  const submit = async () => {
    try {
      await api.post(`/admin/access-requests/${request.id}/reject`, { note: note || null });
      toast.success("Request rejected");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Reject request from {request.name}?</DialogTitle>
          <DialogDescription>The request will move to the Rejected tab.</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} placeholder="Reason (optional, internal)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="reject-note-input" />
        <DialogFooter>
          <button onClick={onClose} className="rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold" data-testid="reject-cancel-button">Cancel</button>
          <button onClick={submit} className="rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700" data-testid="reject-confirm-button">Reject</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const RequestCard = ({ r, onApprove, onReject }) => (
  <div className="flex flex-col gap-5 rounded-2xl border border-lasso-line bg-white p-6 md:flex-row md:items-center" data-testid={`access-request-${r.id}`}>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-display text-base font-bold" data-testid={`access-request-name-${r.id}`}>{r.name}</p>
        <StatusBadge status={r.status} />
        {r.source === "google" && <span className="rounded-full bg-lasso-tint px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-lasso-deep">via Google</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-zinc-600">
        <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{r.email}</span>
        {r.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{r.phone}</span>}
        {r.community_name && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{r.community_name}</span>}
      </div>
      {r.message && <p className="mt-3 max-w-2xl text-sm italic text-zinc-500">“{r.message}”</p>}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {r.role_requested ? <RoleBadge role={r.role_requested} /> : <span>No role requested</span>}
        <span>Submitted {formatDate(r.created_at)}</span>
        {r.reviewed_by && <span>· {r.status} by {r.reviewed_by} on {formatDate(r.reviewed_at)}</span>}
      </div>
    </div>
    {r.status === "pending" && (
      <div className="flex gap-2">
        <button onClick={() => onReject(r)} className="inline-flex items-center gap-1.5 rounded-full border border-lasso-line px-4 py-2 text-sm font-semibold hover:border-rose-400 hover:text-rose-600" data-testid={`admin-reject-request-btn-${r.id}`}><X className="h-4 w-4" /> Reject</button>
        <button onClick={() => onApprove(r)} className="inline-flex items-center gap-1.5 rounded-full bg-lasso-ink px-4 py-2 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid={`admin-approve-request-btn-${r.id}`}><Check className="h-4 w-4" /> Approve</button>
      </div>
    )}
  </div>
);

export default function AccessRequests() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState(null);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const { regions, communities } = useOrg();

  const load = useCallback(() => {
    api.get("/admin/access-requests", { params: tab === "all" ? {} : { status: tab } }).then((r) => setItems(r.data));
  }, [tab]);

  useEffect(load, [load]);

  const done = () => {
    setApproving(null);
    setRejecting(null);
    load();
  };

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Admin" title="Access requests" description="Review new team members, assign their role and community, and choose how they sign in." />
      <Tabs value={tab} onValueChange={setTab} className="mb-8">
        <TabsList data-testid="access-requests-tabs">
          {["pending", "approved", "rejected", "all"].map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`access-requests-tab-${t}`}>{t}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="space-y-4" data-testid="access-requests-list">
        {items?.length === 0 && <EmptyState title={`No ${tab === "all" ? "" : tab} requests`} testId="access-requests-empty" />}
        {items?.map((r) => <RequestCard key={r.id} r={r} onApprove={setApproving} onReject={setRejecting} />)}
      </div>
      <ApproveDialog request={approving} onClose={() => setApproving(null)} onDone={done} regions={regions} communities={communities} />
      <RejectDialog request={rejecting} onClose={() => setRejecting(null)} onDone={done} />
    </div>
  );
}
